import { type AssignmentDto, todayDayIndex, yearSlotRange } from '@betreuung/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAssignmentRange } from '@/api/queries';
import { useAppData } from '@/app/appData';
import { useNavigation } from '@/app/navigation';
import { TabHeader } from '@/app/TabHeader';
import { useIsDesktop } from '@/app/useMediaQuery';
import { Button } from '@/components/ui/button';
import { de } from '@/i18n/de';
import { MiniMonth } from './MiniMonth';

const MONTHS = Array.from({ length: 12 }, (_, index) => index + 1);

/** Stable identity while the query is loading, so the mini months do not re-derive. */
const NO_ASSIGNMENTS: AssignmentDto[] = [];

/**
 * Year overview: twelve mini months, one day tile each, coloured by the day's
 * dominant person. It is a jumping-off point rather than an editing surface.
 */
export function YearTab() {
  const { persons, now } = useAppData();
  const { yearCursor, stepYear, goToMonth } = useNavigation();
  const isDesktop = useIsDesktop();

  const assignments = useAssignmentRange(yearSlotRange(yearCursor));
  const todayDay = todayDayIndex(now);

  return (
    <div className="pb-4 nav:max-w-[720px]">
      <TabHeader
        title={String(yearCursor)}
        showSettings={!isDesktop}
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              className="hit-44"
              aria-label={de.year.previous}
              onClick={() => stepYear(-1)}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="hit-44"
              aria-label={de.year.next}
              onClick={() => stepYear(1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </>
        }
      />

      <p className="px-4 pt-0.5 pb-2 text-hint text-fg-hint">{de.year.hint}</p>

      <div className="mx-4 rounded-card border border-line bg-card p-4 shadow-card">
        <div className="grid grid-cols-3 gap-x-3.5 gap-y-4">
          {MONTHS.map((month) => (
            <MiniMonth
              key={month}
              year={yearCursor}
              month={month}
              assignments={assignments.data ?? NO_ASSIGNMENTS}
              persons={persons}
              todayDay={todayDay}
              onSelect={goToMonth}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
